import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner } from "react-bootstrap";
import {
    archiveAdminPublicNews,
    createAdminPublicNews,
    fetchAdminPublicNews,
    updateAdminPublicNews,
    uploadAdminPublicNewsImage,
} from "../../http/publicNewsAPI";

const STATUS_OPTIONS = ["DRAFT", "PUBLISHED", "ARCHIVED"];
const TOPIC_OPTIONS = ["blog", "news"];

const EMPTY_FORM = {
    id: null,
    title: "",
    slug: "",
    topic: "blog",
    date: new Date().toISOString().slice(0, 10),
    excerpt: "",
    image: "",
    status: "DRAFT",
    seoTitle: "",
    seoDescription: "",
    canonicalPath: "",
    authorName: "Virtalibry Editorial",
    content: "<p>New public article.</p>",
};

const resolveImageUrl = (image) => {
    if (!image) return "";
    if (/^(data:|https?:\/\/)/i.test(image)) return image;
    const base = String(process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
    return `${base}${image.startsWith("/") ? image : `/${image}`}`;
};

const slugify = (value) => (
    String(value || "")
        .trim()
        .toLowerCase()
        .replace(/['"]/g, "")
        .replace(/[^a-z0-9_-]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 180)
);

const defaultCanonicalPath = (topic, slug) => (
    topic === "news" ? `/project-news/${slug || ""}` : `/blog/${slug || ""}`
);

const postToForm = (post) => ({
    id: post?.id ?? null,
    title: post?.title || "",
    slug: post?.slug || "",
    topic: post?.topic || "blog",
    date: post?.date || (post?.published_at || post?.publishedAt ? new Date(post.published_at || post.publishedAt).toISOString().slice(0, 10) : ""),
    excerpt: post?.excerpt || post?.summary || "",
    image: post?.cover_image || post?.coverImage || post?.image || "",
    status: post?.status || "DRAFT",
    seoTitle: post?.seo_title || post?.seoTitle || "",
    seoDescription: post?.seo_description || post?.seoDescription || "",
    canonicalPath: post?.canonical_path || post?.canonicalPath || "",
    authorName: post?.author_name || post?.authorName || "Virtalibry Editorial",
    content: post?.content_html || post?.contentHtml || post?.content || "",
});

const HtmlEditor = ({ value, onChange, onInsertImage }) => {
    const editorRef = useRef(null);
    const [sourceMode, setSourceMode] = useState(true);

    useEffect(() => {
        if (!sourceMode && editorRef.current && editorRef.current.innerHTML !== value) {
            editorRef.current.innerHTML = value || "";
        }
    }, [value, sourceMode]);

    const commit = () => {
        if (editorRef.current) onChange(editorRef.current.innerHTML);
    };

    const exec = (command, arg = null) => {
        editorRef.current?.focus();
        document.execCommand(command, false, arg);
        commit();
    };

    const makeLink = () => {
        const url = window.prompt("Link URL");
        if (url) exec("createLink", url);
    };

    if (sourceMode) {
        return (
            <>
                <div className="d-flex flex-wrap gap-1 mb-2">
                    <Button size="sm" variant="outline-secondary" onClick={() => setSourceMode(false)}>
                        Visual
                    </Button>
                    <Button size="sm" variant="outline-secondary" onClick={onInsertImage}>
                        Insert Image
                    </Button>
                </div>
                <Form.Control
                    size="sm"
                    as="textarea"
                    rows={18}
                    value={value || ""}
                    onChange={(event) => onChange(event.target.value)}
                    style={{ fontFamily: "Consolas, monospace", fontSize: 13 }}
                />
            </>
        );
    }

    return (
        <>
            <div className="d-flex flex-wrap gap-1 mb-2">
                <Button size="sm" variant="outline-secondary" onMouseDown={(event) => { event.preventDefault(); exec("bold"); }}>B</Button>
                <Button size="sm" variant="outline-secondary" onMouseDown={(event) => { event.preventDefault(); exec("italic"); }}>I</Button>
                <Button size="sm" variant="outline-secondary" onMouseDown={(event) => { event.preventDefault(); exec("formatBlock", "h2"); }}>H2</Button>
                <Button size="sm" variant="outline-secondary" onMouseDown={(event) => { event.preventDefault(); exec("formatBlock", "h3"); }}>H3</Button>
                <Button size="sm" variant="outline-secondary" onMouseDown={(event) => { event.preventDefault(); exec("formatBlock", "p"); }}>P</Button>
                <Button size="sm" variant="outline-secondary" onMouseDown={(event) => { event.preventDefault(); exec("insertUnorderedList"); }}>UL</Button>
                <Button size="sm" variant="outline-secondary" onMouseDown={(event) => { event.preventDefault(); exec("insertOrderedList"); }}>OL</Button>
                <Button size="sm" variant="outline-secondary" onMouseDown={(event) => { event.preventDefault(); makeLink(); }}>Link</Button>
                <Button size="sm" variant="outline-secondary" onClick={onInsertImage}>Image</Button>
                <Button size="sm" variant="outline-secondary" onMouseDown={(event) => { event.preventDefault(); exec("removeFormat"); }}>Clear</Button>
                <Button size="sm" variant="outline-secondary" onClick={() => { commit(); setSourceMode(true); }}>HTML</Button>
            </div>
            <div
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                onBlur={commit}
                className="border rounded bg-white p-3"
                style={{ minHeight: 360, maxHeight: 620, overflow: "auto" }}
            />
        </>
    );
};

const PublicPostPreview = ({ form }) => (
    <article>
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
            <div>
                <Badge bg={form.topic === "news" ? "success" : "primary"} className="mb-2">
                    {form.topic === "news" ? "Project News" : "Blog"}
                </Badge>
                <h4 className="mb-1">{form.title || "Untitled public post"}</h4>
                <p className="text-muted fst-italic small mb-0">{form.date}</p>
            </div>
            <span className="small text-muted">{form.canonicalPath || defaultCanonicalPath(form.topic, form.slug)}</span>
        </div>

        {form.excerpt && <p className="text-muted">{form.excerpt}</p>}
        {form.image && (
            <img
                src={resolveImageUrl(form.image)}
                alt={form.title}
                style={{
                    width: "100%",
                    maxHeight: 280,
                    objectFit: "cover",
                    borderRadius: 8,
                    marginBottom: 16,
                }}
            />
        )}
        <div
            className="text-muted"
            style={{ lineHeight: 1.65 }}
            dangerouslySetInnerHTML={{ __html: form.content || "" }}
        />
    </article>
);

const AdminPublicNews = () => {
    const [filters, setFilters] = useState({ search: "", topic: "", status: "", page: 1, limit: 50 });
    const [listData, setListData] = useState({ items: [], total: 0, totalPages: 1 });
    const [selectedId, setSelectedId] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [listLoading, setListLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const [lastInlineImage, setLastInlineImage] = useState("");

    const validationIssues = useMemo(() => {
        const issues = [];
        if (!form.title.trim()) issues.push("Title is required.");
        if (!form.slug.trim()) issues.push("Slug is required.");
        if (/[/?#\\\s]/.test(form.slug)) issues.push("Slug must not contain spaces, slashes, query, or hash characters.");
        if (!TOPIC_OPTIONS.includes(form.topic)) issues.push("Topic is invalid.");
        if (!form.date) issues.push("Date is required.");
        if (!STATUS_OPTIONS.includes(form.status)) issues.push("Status is invalid.");
        if (!form.seoTitle.trim()) issues.push("SEO title is recommended.");
        if (!form.seoDescription.trim()) issues.push("SEO description is recommended.");
        return issues;
    }, [form]);

    const loadList = useCallback(async (preferredId = null) => {
        setListLoading(true);
        setError("");

        try {
            const data = await fetchAdminPublicNews({
                search: filters.search || undefined,
                topic: filters.topic || undefined,
                status: filters.status || undefined,
                page: filters.page,
                limit: filters.limit,
            });
            const items = data.items || [];
            setListData(data);
            setSelectedId((currentId) => {
                const nextItem = items.find((item) => item.id === (preferredId || currentId)) || items[0] || null;
                if (nextItem) setForm(postToForm(nextItem));
                return nextItem?.id || null;
            });
        } catch (err) {
            setError(err?.response?.data?.message || "Failed to load public news/blog posts.");
        } finally {
            setListLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        loadList();
    }, [loadList]);

    const updateFilter = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value, page: key === "page" ? value : 1 }));
    };

    const updateForm = (key, value) => {
        setForm((prev) => {
            const next = { ...prev, [key]: value };
            if (key === "topic" && (!prev.canonicalPath || prev.canonicalPath === defaultCanonicalPath(prev.topic, prev.slug))) {
                next.canonicalPath = defaultCanonicalPath(value, prev.slug);
            }
            if (key === "slug" && (!prev.canonicalPath || prev.canonicalPath === defaultCanonicalPath(prev.topic, prev.slug))) {
                next.canonicalPath = defaultCanonicalPath(prev.topic, value);
            }
            return next;
        });
        setMessage("");
        setError("");
    };

    const newPost = () => {
        setSelectedId(null);
        setForm({
            ...EMPTY_FORM,
            date: new Date().toISOString().slice(0, 10),
        });
        setMessage("");
        setError("");
    };

    const selectPost = (item) => {
        setSelectedId(item.id);
        setForm(postToForm(item));
        setMessage("");
        setError("");
    };

    const save = async (statusOverride = null) => {
        const nextStatus = statusOverride || form.status;
        const slug = form.slug.trim();
        const topic = form.topic || "blog";
        const payload = {
            slug,
            title: form.title.trim(),
            topic,
            excerpt: form.excerpt,
            content_html: form.content,
            cover_image: form.image,
            author_name: form.authorName || "Virtalibry Editorial",
            published_at: form.date,
            status: nextStatus,
            seo_title: form.seoTitle || form.title,
            seo_description: form.seoDescription || form.excerpt,
            canonical_path: form.canonicalPath || defaultCanonicalPath(topic, slug),
        };

        const issues = [];
        if (!payload.title) issues.push("Title is required.");
        if (!payload.slug) issues.push("Slug is required.");
        if (!TOPIC_OPTIONS.includes(payload.topic)) issues.push("Topic is invalid.");
        if (issues.length) {
            setError(issues.join(" "));
            return;
        }

        setSaving(true);
        setError("");
        setMessage("");

        try {
            const saved = form.id
                ? await updateAdminPublicNews(form.id, payload)
                : await createAdminPublicNews(payload);
            setSelectedId(saved.id);
            setForm(postToForm(saved));
            setMessage(`Public ${saved.topic === "news" ? "news" : "blog post"} ${form.id ? "saved" : "created"}.`);
            await loadList(saved.id);
        } catch (err) {
            setError(err?.response?.data?.message || "Failed to save public news/blog post.");
        } finally {
            setSaving(false);
        }
    };

    const archive = async () => {
        if (!form.id) return;
        setSaving(true);
        setError("");
        setMessage("");

        try {
            const archived = await archiveAdminPublicNews(form.id);
            setForm(postToForm(archived));
            setMessage("Public news/blog post archived.");
            await loadList(archived.id);
        } catch (err) {
            setError(err?.response?.data?.message || "Failed to archive public news/blog post.");
        } finally {
            setSaving(false);
        }
    };

    const uploadImage = async (file, target) => {
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            setError("Please choose an image file.");
            return;
        }

        const formData = new FormData();
        formData.append("image", file);
        formData.append("target", target);

        setUploading(true);
        setError("");
        setMessage("");

        try {
            const data = await uploadAdminPublicNewsImage(formData);
            if (target === "inline") {
                const imageUrl = data?.image || "";
                const snippet = `<img src="${imageUrl}" alt="" class="article-inline-image" style="max-width:70%;margin:20px auto;display:block;border-radius:8px;box-shadow:0 2px 6px rgba(0,0,0,0.15);" />`;
                updateForm("content", `${form.content || ""}\n\n${snippet}`);
                setLastInlineImage(imageUrl);
                setMessage("Inline image uploaded and inserted into HTML.");
            } else {
                updateForm("image", data?.image || "");
                setMessage("Cover image uploaded. Save the post to apply it.");
            }
        } catch (err) {
            setError(err?.response?.data?.message || "Failed to upload image.");
        } finally {
            setUploading(false);
        }
    };

    const insertImageByUrl = () => {
        const imageUrl = window.prompt("Image URL", lastInlineImage || "/img/public-news-inline/");
        if (!imageUrl) return;
        const snippet = `<img src="${imageUrl}" alt="" class="article-inline-image" style="max-width:70%;margin:20px auto;display:block;border-radius:8px;box-shadow:0 2px 6px rgba(0,0,0,0.15);" />`;
        updateForm("content", `${form.content || ""}\n\n${snippet}`);
        setLastInlineImage(imageUrl);
    };

    return (
        <Card className="shadow-sm">
            <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                <span className="fw-semibold text-success">Public News / Blog</span>
                <div className="d-flex flex-wrap gap-2">
                    <Button size="sm" variant="outline-secondary" onClick={loadList} disabled={listLoading}>
                        Refresh
                    </Button>
                    <Button size="sm" variant="success" onClick={newPost}>
                        New
                    </Button>
                </div>
            </Card.Header>
            <Card.Body>
                {error && <Alert variant="danger" className="py-2">{error}</Alert>}
                {message && <Alert variant="success" className="py-2">{message}</Alert>}

                <Row className="g-3">
                    <Col lg={4} xs={12}>
                        <Card className="border">
                            <Card.Body>
                                <Row className="g-2 mb-3">
                                    <Col xs={5}>
                                        <Form.Control
                                            size="sm"
                                            placeholder="Search..."
                                            value={filters.search}
                                            onChange={(event) => updateFilter("search", event.target.value)}
                                        />
                                    </Col>
                                    <Col xs={3}>
                                        <Form.Select
                                            size="sm"
                                            value={filters.topic}
                                            onChange={(event) => updateFilter("topic", event.target.value)}
                                        >
                                            <option value="">Topic</option>
                                            {TOPIC_OPTIONS.map((topic) => (
                                                <option key={topic} value={topic}>{topic}</option>
                                            ))}
                                        </Form.Select>
                                    </Col>
                                    <Col xs={4}>
                                        <Form.Select
                                            size="sm"
                                            value={filters.status}
                                            onChange={(event) => updateFilter("status", event.target.value)}
                                        >
                                            <option value="">All</option>
                                            {STATUS_OPTIONS.map((status) => (
                                                <option key={status} value={status}>{status}</option>
                                            ))}
                                        </Form.Select>
                                    </Col>
                                </Row>

                                {listLoading && (
                                    <div className="d-flex align-items-center gap-2 text-muted small mb-2">
                                        <Spinner size="sm" /> Loading public posts...
                                    </div>
                                )}

                                <div className="list-group" style={{ maxHeight: 720, overflow: "auto" }}>
                                    {(listData.items || []).map((item) => (
                                        <button
                                            key={item.id}
                                            type="button"
                                            className={`list-group-item list-group-item-action ${item.id === selectedId ? "active" : ""}`}
                                            onClick={() => selectPost(item)}
                                        >
                                            <div className="d-flex justify-content-between gap-2">
                                                <span className="small text-truncate">{item.title}</span>
                                                <Badge    className="flex-shrink-0 px-2 py-1"  bg={item.status === "PUBLISHED" ? "success" : item.status === "ARCHIVED" ? "secondary" : "warning"} >
                                                    {item.status}
                                                </Badge>
                                            </div>
                                            <div className="d-flex justify-content-between gap-2 small">
                                                {/*<span>{item.topic}</span>*/}
                                                <span className="small text-muted">{item.date}</span>
                                            </div>
                                            {/*<div className="small text-truncate">{item.slug}</div>*/}
                                        </button>
                                    ))}
                                </div>

                                {!listLoading && (listData.items || []).length === 0 && (
                                    <p className="small text-muted mb-0">No public news/blog posts found.</p>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col lg={8} xs={12}>
                        <Card className="border mb-3">
                            <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                                <span className="fw-semibold">{form.id ? `Edit #${form.id}` : "Create public news/blog post"}</span>
                                <div className="d-flex flex-wrap gap-2">
                                    <Button size="sm" variant="outline-secondary" onClick={() => save("DRAFT")} disabled={saving || uploading}>
                                        Save Draft
                                    </Button>
                                    <Button size="sm" variant="success" onClick={() => save("PUBLISHED")} disabled={saving || uploading}>
                                        Publish
                                    </Button>
                                    <Button size="sm" variant="outline-danger" onClick={archive} disabled={!form.id || saving || uploading}>
                                        Archive
                                    </Button>
                                </div>
                            </Card.Header>
                            <Card.Body>
                                {validationIssues.length > 0 && (
                                    <Alert variant="warning" className="py-2">
                                        {validationIssues.join(" ")}
                                    </Alert>
                                )}

                                <Row className="g-3">
                                    <Col md={8} xs={12}>
                                        <Form.Group className="mb-2">
                                            <Form.Label>Title</Form.Label>
                                            <Form.Control
                                                size="sm"
                                                value={form.title}
                                                onChange={(event) => updateForm("title", event.target.value)}
                                                onBlur={() => {
                                                    if (!form.slug && form.title) {
                                                        const nextSlug = slugify(form.title);
                                                        updateForm("slug", nextSlug);
                                                        updateForm("seoTitle", form.seoTitle || form.title);
                                                    }
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={2} xs={6}>
                                        <Form.Group className="mb-2">
                                            <Form.Label>Topic</Form.Label>
                                            <Form.Select size="sm" value={form.topic} onChange={(event) => updateForm("topic", event.target.value)}>
                                                {TOPIC_OPTIONS.map((topic) => (
                                                    <option key={topic} value={topic}>{topic}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={2} xs={6}>
                                        <Form.Group className="mb-2">
                                            <Form.Label>Status</Form.Label>
                                            <Form.Select size="sm" value={form.status} onChange={(event) => updateForm("status", event.target.value)}>
                                                {STATUS_OPTIONS.map((status) => (
                                                    <option key={status} value={status}>{status}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>

                                    <Col md={8} xs={12}>
                                        <Form.Group className="mb-2">
                                            <Form.Label>Slug</Form.Label>
                                            <div className="d-flex gap-2">
                                                <Form.Control size="sm" value={form.slug} onChange={(event) => updateForm("slug", event.target.value)} />
                                                <Button size="sm" variant="outline-secondary" onClick={() => updateForm("slug", slugify(form.title || form.slug))}>
                                                    Slugify
                                                </Button>
                                            </div>
                                        </Form.Group>
                                    </Col>
                                    <Col md={4} xs={12}>
                                        <Form.Group className="mb-2">
                                            <Form.Label>Date</Form.Label>
                                            <Form.Control size="sm" type="date" value={form.date} onChange={(event) => updateForm("date", event.target.value)} />
                                        </Form.Group>
                                    </Col>

                                    <Col xs={12}>
                                        <Form.Group className="mb-2">
                                            <Form.Label>Excerpt</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={3}
                                                size="sm"
                                                value={form.excerpt}
                                                onChange={(event) => updateForm("excerpt", event.target.value)}
                                                onBlur={() => {
                                                    if (!form.seoDescription && form.excerpt) updateForm("seoDescription", form.excerpt);
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={8} xs={12}>
                                        <Form.Group className="mb-2">
                                            <Form.Label>Cover image</Form.Label>
                                            <Form.Control size="sm" value={form.image} onChange={(event) => updateForm("image", event.target.value)} />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4} xs={12}>
                                        <Form.Group className="mb-2">
                                            <Form.Label>Upload cover</Form.Label>
                                            <Form.Control
                                                size="sm"
                                                type="file"
                                                accept="image/*"
                                                disabled={uploading}
                                                onChange={(event) => {
                                                    const file = event.target.files?.[0];
                                                    event.target.value = "";
                                                    uploadImage(file, "preview");
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={6} xs={12}>
                                        <Form.Group className="mb-2">
                                            <Form.Label>SEO title</Form.Label>
                                            <Form.Control size="sm" value={form.seoTitle} onChange={(event) => updateForm("seoTitle", event.target.value)} />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6} xs={12}>
                                        <Form.Group className="mb-2">
                                            <Form.Label>Canonical path</Form.Label>
                                            <Form.Control
                                                size="sm"
                                                value={form.canonicalPath}
                                                placeholder={defaultCanonicalPath(form.topic, form.slug)}
                                                onChange={(event) => updateForm("canonicalPath", event.target.value)}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12}>
                                        <Form.Group className="mb-2">
                                            <Form.Label>SEO description</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={2}
                                                size="sm"
                                                value={form.seoDescription}
                                                onChange={(event) => updateForm("seoDescription", event.target.value)}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={4} xs={12}>
                                        <Form.Group className="mb-2">
                                            <Form.Label>Author</Form.Label>
                                            <Form.Control size="sm" value={form.authorName} onChange={(event) => updateForm("authorName", event.target.value)} />
                                        </Form.Group>
                                    </Col>
                                    <Col md={8} xs={12}>
                                        <Form.Group className="mb-2">
                                            <Form.Label>Upload inline body image</Form.Label>
                                            <Form.Control
                                                size="sm"
                                                type="file"
                                                accept="image/*"
                                                disabled={uploading}
                                                onChange={(event) => {
                                                    const file = event.target.files?.[0];
                                                    event.target.value = "";
                                                    uploadImage(file, "inline");
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col xs={12}>
                                        <Form.Group className="mb-2">
                                            <Form.Label>Content HTML</Form.Label>
                                            <HtmlEditor value={form.content} onChange={(value) => updateForm("content", value)} onInsertImage={insertImageByUrl} />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <div className="d-flex flex-wrap justify-content-end gap-2 mt-3">
                                    <Button size="sm" variant="outline-secondary" onClick={() => save()} disabled={saving || uploading}>
                                        {saving ? "Saving..." : "Save"}
                                    </Button>
                                    <Button size="sm" variant="success" onClick={() => save("PUBLISHED")} disabled={saving || uploading}>
                                        Publish
                                    </Button>
                                </div>
                            </Card.Body>
                        </Card>

                        <Card className="border">
                            <Card.Header className="fw-semibold">Public Preview</Card.Header>
                            <Card.Body>
                                <PublicPostPreview form={form} />
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Card.Body>
        </Card>
    );
};

export default AdminPublicNews;
